/**
 * Domain: Person → Device Sync Data Resolution
 *
 * Pure functions that compute what to send to a physical device when syncing a
 * person.  Zero React / side-effect dependencies — fully unit-testable.
 *
 * Key invariants
 * ──────────────
 * 1. Photo URL:  proxy URL  (/api/v1/images/…)  never expires.
 *                presigned S3 URL (X-Amz-Signature=…) expires after 15 min.
 *                Always prefer the non-expiring URL so cached payloads stay valid.
 *
 * 2. validFrom:  use the role's own valid_from (the time the role was created /
 *                the visitor was authorized) — NOT the current clock time.
 *                Sending now() causes a small drift when the sync runs after
 *                authorization and breaks pre-scheduled visitors entirely.
 *                Either way, backdated by DEVICE_VALID_FROM_SKEW_BUFFER_MS before
 *                it reaches a device — see bufferedValidFromIso() below.
 *
 * 3. validUntil / entryWindowExpiresAt:
 *                "entrada" devices should only honour the short entry window
 *                (entry_window_expires_at ≈ now+15 min) — the visitor must enter
 *                while the door is "open", then their biometric is revoked.
 *                "saida"/"passagem" devices should use the full access window
 *                (access_expires_at ≈ end of day / explicit valid_until).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PersonPhotoSource {
  /** Non-expiring proxy URL: /api/v1/images/{bucket}/{key}  */
  profile_picture_url?: string | null;
  /** Presigned S3 URL — expires 15 min after the API call  */
  identity_photo_url?: string | null;
}

export interface RoleTimingSource {
  /** ISO-8601 — when the role was created / visitor was authorized */
  valid_from?: string | null;
  /** ISO-8601 — latest allowed access time (end-of-day or explicit) */
  valid_until?: string | null;
  role_metadata?: {
    /** ISO-8601 — short window for the *entry* door (≈ now + 10–15 min) */
    entry_window_expires_at?: string | null;
    /** ISO-8601 — full-day access window (same as valid_until or 23:59) */
    access_expires_at?: string | null;
  } | null;
}

export type DeviceDirection = 'entrada' | 'saida' | 'passagem' | undefined | null;

export interface DeviceSyncTimes {
  validFrom: string;
  validUntil: string;
}

// ── Device clock-skew buffer ─────────────────────────────────────────────────

/**
 * Some facial-recognition/RFID devices (Hikvision's `beginTime`, in particular) reject or
 * delay recognizing a person whose validity window starts at exactly "now" if the device's
 * own clock lags the app's, even slightly. Backdating the value we send absorbs that skew
 * without weakening the real access window — shifting a start time earlier only ever makes
 * access valid *sooner*, never later. Mirrors the same buffer applied server-side
 * (condo-manager's `deviceValidFromSkewBuffer`) for the cloud/iot-connect sync path — this
 * is the equivalent for the Electron app's direct local-network device sync.
 */
export const DEVICE_VALID_FROM_SKEW_BUFFER_MS = 10 * 60 * 1000;

/**
 * Backdates a validFrom source (or "now" when none is given) by the device clock-skew
 * buffer. Use this instead of `new Date().toISOString()` (or a raw `source || new Date()...`
 * fallback) anywhere a validFrom/beginTime value is built to send directly to a device.
 */
export function bufferedValidFromIso(source?: string | null, now: number = Date.now()): string {
  const base = source ? new Date(source).getTime() : now;
  return new Date(base - DEVICE_VALID_FROM_SKEW_BUFFER_MS).toISOString();
}

// ── resolvePhotoUrl ───────────────────────────────────────────────────────────

/**
 * Pick the best available photo URL for device sync.
 *
 * Priority: proxy URL (never expires) > presigned S3 URL (expires 15 min).
 * Returns null when neither URL is available.
 */
export function resolvePhotoUrl(person: PersonPhotoSource): string | null {
  return person.profile_picture_url || person.identity_photo_url || null;
}

// ── resolveDeviceSyncTimes ────────────────────────────────────────────────────

/**
 * Compute validFrom / validUntil for a specific device direction.
 *
 * @param role     - The PersonRole (or equivalent) that holds timing data.
 * @param direction - The device's direction tag ('entrada', 'saida', 'passagem',
 *                   or undefined/null for unknown direction).
 * @param now      - Injectable clock (defaults to Date.now()) for testability.
 *
 * Rules:
 *  validFrom  = role.valid_from  (authorization time, not sync time)
 *  validUntil = entrada → entry_window_expires_at (short window, default 15 min)
 *               saida / passagem / unknown → access_expires_at  OR  valid_until
 *                                             (long window, default end-of-day)
 */
export function resolveDeviceSyncTimes(
  role: RoleTimingSource,
  direction: DeviceDirection,
  now: number = Date.now(),
): DeviceSyncTimes {
  // validFrom: prefer role's own valid_from; fall back to now. Either way, backdated by
  // the device clock-skew buffer (see bufferedValidFromIso) before it reaches a device.
  const validFrom = bufferedValidFromIso(role.valid_from, now);

  const isEntrada = direction === 'entrada';

  let validUntil: string;
  if (isEntrada) {
    // Entry door: visitor must scan within the short entry window
    validUntil =
      role.role_metadata?.entry_window_expires_at ||
      new Date(now + 15 * 60 * 1000).toISOString(); // default 15 min
  } else {
    // Exit / passagem / unknown: use the full-day access window
    validUntil =
      role.role_metadata?.access_expires_at ||
      role.valid_until ||
      (() => { const d = new Date(now); d.setUTCHours(23, 59, 59, 0); return d.toISOString(); })();
  }

  return { validFrom, validUntil };
}

/** A vehicle as the person modal holds it — only the device-relevant fields matter here. */
export interface PersonVehicleSource {
  rfid_tag?: string | null;
  plate?: string | null;
}

export interface PersonSyncSource extends PersonPhotoSource {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  cpf?: string | null;
  vehicles?: PersonVehicleSource[] | null;
}

export interface FingerprintTemplate {
  templateB64: string;
}

/**
 * The vendor-agnostic payload a person sync sends to every device.
 *
 * It exists because two buttons in Permissões each built their own: the zone sync and the
 * device section's "Sincronizar RFID". Only the second carried fingerprint templates, so
 * which button you pressed decided whether a fingerprint reader got anything usable. One
 * builder means one answer, and the device table can go back to being a read-only report.
 */
export interface PersonSyncOptions {
  /**
   * Photo already downloaded WITH auth, as a data URI. The device layer cannot fetch an
   * authenticated URL itself, so a caller that pre-fetched must pass it here.
   */
  photoDataUri?: string;
  /** Visitor access window — omitted entirely for residents and staff. */
  isVisitor?: boolean;
  validFrom?: string;
  validUntil?: string;
}

export function buildPersonSyncPayload(
  person: PersonSyncSource,
  fingerprintTemplates?: FingerprintTemplate[] | null,
  options: PersonSyncOptions = {},
): Record<string, any> {
  const vehicles = person.vehicles ?? [];
  const photo = options.photoDataUri;
  return {
    person_id: person.id,
    first_name: person.first_name,
    last_name: person.last_name,
    cpf: person.cpf,
    identity_photo_url: photo || person.identity_photo_url,
    profile_picture_url: photo || person.profile_picture_url,
    isAllowed: true,
    rfid_tags: vehicles.map(v => v.rfid_tag).filter((t): t is string => !!t),
    vehicle_plates: vehicles.map(v => v.plate).filter((p): p is string => !!p),
    // Always a list: a reader that supports fingerprints must not have to guess whether an
    // absent field means "none" or "the caller forgot".
    fingerprint_templates: fingerprintTemplates ?? [],
    // Spread rather than set: a resident's payload must carry no visitor keys at all,
    // since the adapters treat their presence as "this is a visitor".
    ...(options.isVisitor
      ? { isVisitor: true, validFrom: options.validFrom, validUntil: options.validUntil }
      : {}),
  };
}
